import { useRef, useCallback } from "react";
import ReactDOM from "react-dom/client";
import mapboxgl from "mapbox-gl";

/**
 * Hook for managing map pins/markers - scoped to component instance
 * @returns {Object} Pin management functions
 */
export const useMapPins = () => {
  const pinsRef = useRef([]);
  const namedPinsRef = useRef({});

  /**
   * Add a pin to the map
   * @param {Object} map - Mapbox map instance
   * @param {React.Component} PinComponent - React component to render as pin
   * @param {Array} point - [lng, lat] coordinates
   * @param {number} offset - Y offset for the marker
   * @returns {Object} The created marker
   */
  const addPin = useCallback((map, PinComponent, point, offset = -21) => {
    if (!map) return null;

    const markerContainer = document.createElement("div");
    const root = ReactDOM.createRoot(markerContainer);
    root.render(<PinComponent />);

    const marker = new mapboxgl.Marker(markerContainer, {
      offset: [0, offset],
    })
      .setLngLat(point)
      .addTo(map);

    pinsRef.current = [...pinsRef.current, marker];
    return marker;
  }, []);

  /**
   * Remove all pins
   */
  const removeAllPins = useCallback(() => {
    pinsRef.current.forEach((pin) => {
      pin.remove();
    });
    pinsRef.current = [];
  }, []);

  /**
   * Add a named group of pins
   * @param {string} name - Group name
   * @param {Object} map - Mapbox map instance
   * @param {React.Component} PinComponent - React component to render as pin
   * @param {Array} point - [lng, lat] coordinates
   * @param {number} offset - Y offset for the marker
   * @returns {Object} The created marker
   */
  const addNamedPin = useCallback((name, map, PinComponent, point, offset = -21) => {
    if (!map) return null;

    const markerContainer = document.createElement("div");
    const root = ReactDOM.createRoot(markerContainer);
    root.render(<PinComponent />);

    if (!namedPinsRef.current[name]) {
      namedPinsRef.current[name] = [];
    }

    const marker = new mapboxgl.Marker(markerContainer, {
      offset: [0, offset],
    })
      .setLngLat(point)
      .addTo(map);

    namedPinsRef.current[name] = [...namedPinsRef.current[name], marker];
    return marker;
  }, []);

  /**
   * Remove named pins by group name
   * @param {string} name - Group name
   */
  const removeNamedPins = useCallback((name) => {
    namedPinsRef.current[name]?.forEach((pin) => {
      pin.remove();
    });
    namedPinsRef.current[name] = [];
  }, []);

  /**
   * Remove all named pins
   */
  const removeAllNamedPins = useCallback(() => {
    Object.keys(namedPinsRef.current).forEach((name) => {
      namedPinsRef.current[name]?.forEach((pin) => {
        pin.remove();
      });
    });
    namedPinsRef.current = {};
  }, []);

  /**
   * Cleanup all pins (for use in useEffect cleanup)
   */
  const cleanup = useCallback(() => {
    removeAllPins();
    removeAllNamedPins();
  }, [removeAllPins, removeAllNamedPins]);

  return {
    addPin,
    removeAllPins,
    addNamedPin,
    removeNamedPins,
    removeAllNamedPins,
    cleanup,
  };
};
